import "./UwPivotTable.scss";
import React, { useMemo, useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, TableSortLabel, Select, MenuItem } from "@mui/material";
import { useSelector } from "react-redux";
import { GridLoader, ScaleLoader } from "react-spinners";
import newRequest from "../../utils/newRequest";
import { motion } from "framer-motion";
import numeral from "numeral";
import { muiSelectStyles_dark, muiSelectStyles_light } from "../ui/CustomSelect.jsx";
import { FiSearch } from "react-icons/fi";
import ExportToExcelButton from "../exportToExcelButton/ExportToExcelButton.jsx";
import CircularButton, { CustomLoadingButton } from "../ui/CustomButton.jsx";
import OnScreenReports from "../onScreenReports/OnScreenReports.jsx";
import { IoClose, IoDocumentTextSharp } from "react-icons/io5";

const UwPivotTable = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { currentTheme } = useSelector((state) => state.theme);
    const [isProcessing, setIsProcessing] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);

    const fetchDocuments = async () => {
        try {

            // Simulate delay for 5 seconds
            // await new Promise((resolve) => setTimeout(resolve, 5000));
            const res = await newRequest.post("/uwPolicy/findAll", {
                pl_org_code: currentUser.usr_org_code,
                pl_type: "pol",
                result_limit: 30
            }
            );
            setDocuments(res.data);

        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        const loadDocuments = async () => {
            setIsLoading(true);
            await fetchDocuments();
            setIsLoading(false);
        };
        loadDocuments();
    }, []);


    const [filter, setFilter] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [displayMetric, setDisplayMetric] = useState("totalFcPremium");

    const pivotData = useMemo(() => {
        const carriers = {};
        const products = new Set();
        const productTotals = {};
        const carrierTotals = {};
        let grandTotal = 0;

        documents.forEach((item) => {
            const carrierKey = `${item.carrier_details_xx.ent_code} - ${item.carrier_details_xx.ent_name}`;
            const productKey = `${item.pl_product_code} - ${item.product_name_xx}`;

            products.add(productKey);

            if (!carriers[carrierKey]) {
                carriers[carrierKey] = {};
                carrierTotals[carrierKey] = 0;
            }

            if (!carriers[carrierKey][productKey]) {
                carriers[carrierKey][productKey] = { totalFcPremium: 0, commissionNetFc: 0 };
            }

            if (!productTotals[productKey]) {
                productTotals[productKey] = 0;
            }

            // Ensure the values are treated as numbers
            const totalFcPremium = Number(item.total_fc_premium) || 0;
            const commissionNetFc = Number(item.commission_net_fc) || 0;

            let metricValue = displayMetric === "totalFcPremium" ? totalFcPremium : commissionNetFc;

            if (item.drcr_flag_xx === "D") {
                carriers[carrierKey][productKey].totalFcPremium += totalFcPremium;
                carriers[carrierKey][productKey].commissionNetFc += commissionNetFc;
            } else if (item.drcr_flag_xx === "C") {
                carriers[carrierKey][productKey].totalFcPremium -= totalFcPremium;
                carriers[carrierKey][productKey].commissionNetFc -= commissionNetFc;
            }

            // Update totals based on the current displayMetric
            productTotals[productKey] += item.drcr_flag_xx === "D" ? metricValue : -metricValue;
            carrierTotals[carrierKey] += item.drcr_flag_xx === "D" ? metricValue : -metricValue;
            grandTotal += item.drcr_flag_xx === "D" ? metricValue : -metricValue;
        });


        // Sort carriers based on the current displayMetric
        let sortedCarriers = Object.entries(carriers);
        if (sortConfig.key) {
            sortedCarriers.sort((a, b) => {
                const valueA = a[1][sortConfig.key]?.[displayMetric] || 0;
                const valueB = b[1][sortConfig.key]?.[displayMetric] || 0;
                return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
            });
        }

        return {
            carriers: Object.fromEntries(sortedCarriers),
            products: Array.from(products),
            productTotals,
            carrierTotals,
            grandTotal,
        };
    }, [documents, sortConfig, displayMetric]);


    return (
        <div className="uw-pivot-table">
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <div className="pivot-content">
                    <div className="pivot-navbar">
                        <div className="pivot-navbar-left">
                            <h1 className="main-title">{`Viewing ${displayMetric === "totalFcPremium" ? "Premium" : "Net Commission"}`}</h1>
                        </div>
                        <div className="pivot-navbar-right">
                            <div className="search-input-wrapper" style={{ width: "250px" }}>
                                <FiSearch className="search-input-icon" />
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder="Search carrier..."
                                    onChange={(e) => setFilter(e.target.value.toLowerCase())} />
                            </div>
                            <Select
                                id="unit_status"
                                name="unit_status"
                                height="36px"
                                value={displayMetric}
                                onChange={(e) => setDisplayMetric(e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={currentTheme === "dark" ? muiSelectStyles_dark().select : muiSelectStyles_light().select}
                                MenuProps={{
                                    PaperProps: {
                                        sx: currentTheme === "dark" ? muiSelectStyles_dark().menu.paper : muiSelectStyles_light().menu.paper
                                    }
                                }}
                            >
                                <MenuItem value="totalFcPremium">Premium</MenuItem>
                                <MenuItem value="commissionNetFc">Net Comm</MenuItem>
                            </Select>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="none"
                                endIcon={<IoDocumentTextSharp />}
                                onClick={() => setReportsDialogOpen(true)}>
                                Reports
                            </CustomLoadingButton>
                            <ExportToExcelButton
                                data={[
                                    // Export table rows
                                    ...Object.entries(pivotData.carriers)
                                        .filter(([carrier]) => carrier.toLowerCase().includes(filter)) // Apply filter
                                        .map(([carrier, productData]) => {
                                            let row = { "Carrier": carrier }; // First column
                                            pivotData.products.forEach((product) => {
                                                row[product] = productData[product]
                                                    ? numeral(productData[product][displayMetric] || 0).format("0,0.00")
                                                    : "0.00";
                                            });
                                            row["Total"] = numeral(pivotData.carrierTotals[carrier] || 0).format("0,0.00"); // Total column
                                            return row;
                                        }),

                                    // Export total row at the end
                                    {
                                        "Carrier": "Total", // Label for total row
                                        ...Object.fromEntries(
                                            pivotData.products.map((product) => [
                                                product,
                                                numeral(pivotData.productTotals[product] || 0).format("0,0.00"),
                                            ])
                                        ),
                                        "Total": numeral(pivotData.grandTotal || 0).format("0,0.00") // Grand total
                                    }
                                ]}
                                exportColumns={["Carrier", ...pivotData.products, "Total"]}
                                fileName="Documents.xlsx"
                                currentTheme={currentTheme}
                                borderRadiusType="right"
                            />
                        </div>
                    </div>
                    <div className="pivot-table">
                        <div className="table-wrapper">
                            <table className="table">
                                <thead className="tablehead">
                                    <tr className="tablerow">
                                        <th className="table-cell" style={{ whiteSpace: "nowrap", width: "250px" }}>
                                            Carrier
                                        </th>
                                        {pivotData.products.map((product) => (
                                            <th align="right" key={product} style={{ whiteSpace: "nowrap", width: "250px", textAlign: "right" }}>
                                                <h1 className="sub-title">{product.split(" - ")[1]}</h1>
                                                <p className="sub-title soft" style={{ fontSize: "12px" }}>{product.split(" - ")[0]}</p>
                                            </th>
                                        ))}
                                        <th align="right" style={{ whiteSpace: "nowrap", width: "250px", fontWeight: "bold" }}>
                                            Total
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="tablebody">
                                    {Object.entries(pivotData.carriers)
                                        .filter(([carrier]) => carrier.toLowerCase().includes(filter))
                                        .map(([carrier, productData]) => (
                                            <tr key={carrier} className="tablerow">
                                                <td className="table-cell left" style={{ whiteSpace: "nowrap", width: "250px" }}>
                                                    <h1 className="sub-title">{carrier.split(" - ")[1]}</h1>
                                                    <p className="sub-title soft" style={{ fontSize: "12px" }}>{carrier.split(" - ")[0]}</p>
                                                </td>
                                                {pivotData.products.map((product) => (
                                                    <td key={product} align="right" style={{ whiteSpace: "nowrap", width: "250px", textAlign: "right" }}>
                                                        {productData[product] ? numeral(productData[product][displayMetric] || 0).format("0,0.00") : "0.00"}
                                                    </td>
                                                ))}
                                                <td align="right" style={{ fontWeight: "bold", textAlign: "right" }}>
                                                    {numeral(pivotData.carrierTotals[carrier] || 0).format("0,0.00")}
                                                </td>
                                            </tr>
                                        ))}
                                    <tr className="tablerow">
                                        <td align="right" style={{ fontWeight: "bold", textAlign: "right" }}>
                                            Total
                                        </td>
                                        {pivotData.products.map((product) => (
                                            <td key={product} align="right" style={{ fontWeight: "bold", textAlign: "right" }}>
                                                {numeral(pivotData.productTotals[product] || 0).format("0,0.00")}
                                            </td>
                                        ))}
                                        <td align="right" style={{ fontWeight: "bold", textAlign: "right" }}>
                                            {numeral(pivotData.grandTotal || 0).format("0,0.00")}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <GridLoader
                            color="#F44336"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>
            )}
            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">Reports</h1>
                            <CircularButton currentTheme={currentTheme} onClick={() => setReportsDialogOpen(false)} icon={<IoClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={["statements"]}
                                documentData={{
                                    docNo: "",
                                    docType: "Box",
                                    docId: "",
                                    clientEmail: "",
                                    clientEntCode: "",
                                    clientEntName: "",
                                    dateFm: "",
                                    dateTo: "",
                                    productCode: "",
                                    bizUnit: "",
                                    currencyCode: "",
                                }} />
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default UwPivotTable;
